import { SessionPresentation } from '@/components/simulation/SessionPresentation';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useAnamAvatar } from '@/hooks/useAnamAvatar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Video, VideoOff, Camera, Mic } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { CameraCapture, CapturedFrame } from '@/components/simulation/CameraCapture';
import { SessionTimer } from '@/components/simulation/SessionTimer';
import { ResultGenerationDialog } from '@/components/simulation/ResultGenerationDialog';
import { consolidateTranscript } from '@/lib/evaluationCriteria';
import { reportUserFacingError } from '@/lib/errorReporter';
import { stopMediaRecorder } from '@/lib/mediaRecorder';
import fixWebmDuration from 'fix-webm-duration';
import {
  buildCoachingFirstMessage,
  buildCoachingMemoryPrompt,
  fetchCoachingMemoryProfile,
  getLearnerDisplayName,
  type CoachingMemoryProfile
} from '@/lib/coachingMemory';
import { buildAgentPrompt } from '@/lib/prompt';
import {
  shouldAutoCompleteCoachingSession,
  withCoachingVoiceContract,
} from '@/lib/coachingRuntime';
import {
  buildCoachKnowledgeQuery,
  coachKnowledgePassageKey,
  formatCoachKnowledgePassages,
  formatRuntimeCoachKnowledgeContext,
  retrieveCoachKnowledge,
  retrieveRuntimeCoachKnowledge,
} from '@/lib/coachingKnowledge';
import { usesV2Prompt } from '@/lib/prompt/runtime';

function getAgentKnowledgePassages(
  currentAgent: Record<string, unknown> | null,
  currentTemplate: Record<string, unknown> | null,
): string[] {
  return formatCoachKnowledgePassages(retrieveCoachKnowledge({
    knowledgeSources: currentAgent?.knowledge_sources,
    query: buildCoachKnowledgeQuery(currentAgent || {}, currentTemplate),
  }));
}

export default function CoachingSessionPage() {
  const { id: agentId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { tenant } = useTenant();
  const { toast } = useToast();
  
  // Get pathway context from URL params (used for Learning Pathway flow)
  const pathwayId = searchParams.get('pathway');
  const moduleId = searchParams.get('module');
  
  const [agent, setAgent] = useState<any>(null);
  const [template, setTemplate] = useState<any>(null);
  const [coachingMemory, setCoachingMemory] = useState<CoachingMemoryProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const [cameraInitialized, setCameraInitialized] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const cameraOnRef = useRef(false);
  cameraOnRef.current = cameraOn;
  const [recordSession, setRecordSession] = useState(true);
  
  // Frame capture state
  const capturedFramesRef = useRef<CapturedFrame[]>([]);
  const [frameCount, setFrameCount] = useState(0);
  
  // Audio recording refs
  const audioChunksRef = useRef<Blob[]>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioDestinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const audioRecordingStartRef = useRef<number | null>(null);
  const agentAudioAttachedRef = useRef<boolean>(false);
  const videoAudioContextRef = useRef<AudioContext | null>(null);

  // Video recording refs
  const videoChunksRef = useRef<Blob[]>([]);
  const videoRecorderRef = useRef<MediaRecorder | null>(null);
  const videoRecordingStartRef = useRef<number | null>(null);
  const videoRecordingStartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const videoRecordingPromiseRef = useRef<Promise<boolean> | null>(null);
  const sessionActiveRef = useRef(false);
  const autoEndSessionRef = useRef<() => void>(() => undefined);
  const autoEndTimerRef = useRef<number | null>(null);
  const injectedRuntimeKnowledgeRef = useRef<Set<string>>(new Set());
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const uploadIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const uploadedChunksRef = useRef<number>(0);
  const userCameraVideoRef = useRef<HTMLVideoElement | null>(null);

  const handleCameraVideoElementReady = useCallback((video: HTMLVideoElement | null) => {
    userCameraVideoRef.current = video;
  }, []);

  // Guard against double-clicks
  const isProcessingRef = useRef(false);

  // Generate stable session ID
  const sessionId = useMemo(() => crypto.randomUUID(), []);
  const retrievedKnowledgePassages = useMemo(
    () => (agent && template ? getAgentKnowledgePassages(agent, template) : []),
    [agent, template],
  );

  const getRuntimeKnowledgeForUserSpeech = (utterance: string): string | null => {
    const passages = retrieveRuntimeCoachKnowledge(agent?.knowledge_sources, utterance);
    const novelPassages = passages.filter((passage) => (
      !injectedRuntimeKnowledgeRef.current.has(coachKnowledgePassageKey(passage))
    ));
    for (const passage of novelPassages) {
      injectedRuntimeKnowledgeRef.current.add(coachKnowledgePassageKey(passage));
    }
    return formatRuntimeCoachKnowledgeContext(novelPassages);
  };

  const stripHtml = (value: string | null | undefined): string => {
    if (!value) return '';
    return value
      .replace(/<[^>]*>/g, ' ')
      .replace(/\{\{[^}]*\}\}/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Build coaching system prompt
  const buildCoachingPrompt = (agent: any, memory: CoachingMemoryProfile | null): string => {
    const agentInstructions = stripHtml(agent.instructions);
    const agentKnowledge = retrievedKnowledgePassages.length
      ? `\n\nRELEVANT AGENT KNOWLEDGE:\nUse only these ranked passages from this coach's uploaded sources when they are relevant. Preserve source boundaries and do not invent missing detail.\n${retrievedKnowledgePassages.join('\n\n')}`
      : '';
    const coachRole = agent.occupation || agent.role || 'professional coach';
    const agentFocus = stripHtml(agent.roleplay_focus || agent.brief_description || agent.scenario_context || '');
    const personaName = String(agent.persona_name || agent.name || 'Coach').trim();
    const memoryPrompt = buildCoachingMemoryPrompt(
      memory || {
        learnerName: getLearnerDisplayName(user, profile),
        totalSessionsFound: 0,
        items: []
      }
    );

    const prompt = `You are ${personaName}, ${coachRole}.

AGENT PROFILE IS THE SOURCE OF TRUTH:
- Your persona, coaching purpose, behaviour, opening style, and knowledge come from the current agent profile below.
- Do not behave like another coach or reuse a previous coach's purpose.
- If the output template topic conflicts with the agent profile, follow the agent profile during the live conversation.

CURRENT COACH AGENT PROFILE:
Name: ${agent.name}
Spoken identity: ${personaName}
Role / title: ${coachRole}
${agentFocus ? `Focus / brief: ${agentFocus}` : ''}
${agentInstructions ? `Instructions:\n${agentInstructions}` : ''}
${agent.first_message ? `Preferred opening line:\n${stripHtml(agent.first_message)}` : ''}
${agentKnowledge}
${memoryPrompt}

COACHING STYLE: ${agent.coaching_style || 'consultative'}
${agent.coaching_style === 'challenger' ? '- Challenge assumptions and push for deeper thinking\n- Ask tough questions that reveal blind spots\n- Be direct and provocative when needed' : ''}
${agent.coaching_style === 'consultative' ? '- Be collaborative and supportive\n- Ask open-ended questions\n- Guide through active listening and reflection' : ''}
${agent.coaching_style === 'socratic' ? '- Use the Socratic method\n- Guide to answers through strategic questions\n- Help them discover insights themselves' : ''}

BEHAVIOR GUIDELINES:
- Stay in character as ${personaName} throughout
- Follow the purpose and conversation pathway defined in the agent profile
- Ask probing follow-up questions based on responses
- Keep responses concise (2-3 sentences typically)
- Show empathy and active listening
- Build rapport and create psychological safety
- Do not break character or mention you are an AI

SESSION ENDING PROTOCOL (CRITICAL):
When the participant indicates they want to end OR when you have gathered sufficient information:
- Provide a brief, warm closing (1-2 sentences maximum)
- Say something like: "Thanks for the conversation. You can review your next steps shortly."
- Do NOT summarise everything discussed in detail
- Do NOT list all the points you collected
- Keep the closing under 15 seconds
- End naturally and stay in character
- Finish the closing with the exact final word: "Goodbye"`;

    return withCoachingVoiceContract(prompt);
  };

  const anam = useAnamAvatar({
    personaId: agent?.anam_persona_id || '',
    // Strict version gate: V2 only for explicitly converted coaches. V1 coaches
    // keep buildCoachingPrompt exactly as-is. Learner memory is passed as a
    // concise Environment summary in V2 rather than a separate prompt block.
    systemPrompt: agent && template
      ? (usesV2Prompt(agent)
          ? withCoachingVoiceContract(buildAgentPrompt(agent, {
              memorySummary: coachingMemory ? buildCoachingMemoryPrompt(coachingMemory) : null,
              knowledgeExtracts: retrievedKnowledgePassages,
            }).prompt)
          : buildCoachingPrompt(agent, coachingMemory))
      : '',
    firstMessage: agent && template
      ? buildCoachingFirstMessage({
          configuredFirstMessage: agent.first_message,
          learnerName: coachingMemory?.learnerName || getLearnerDisplayName(user, profile),
          memory: coachingMemory || {
            learnerName: getLearnerDisplayName(user, profile),
            totalSessionsFound: 0,
            items: []
          },
          sessionName: template.name
        })
      : undefined,
    onTranscriptUpdate: (messages) => {
    },
    getContextForUserSpeech: getRuntimeKnowledgeForUserSpeech,
    onAssistantSpeechEnd: (utterance) => {
      if (
        !sessionActiveRef.current
        || isProcessingRef.current
        || !shouldAutoCompleteCoachingSession(utterance)
      ) return;

      if (autoEndTimerRef.current) window.clearTimeout(autoEndTimerRef.current);
      autoEndTimerRef.current = window.setTimeout(() => {
        autoEndTimerRef.current = null;
        if (sessionActiveRef.current && !isProcessingRef.current) {
          autoEndSessionRef.current();
        }
      }, 1500);
    },
    onSessionEnd: () => {
    },
    onError: (error) => {
      console.error('Anam error:', error);
      toast({
        title: 'Connection Error',
        description: error,
        variant: 'destructive'
      });
    }
  });

  // Frame capture handler
  const handleFrameCaptured = (frame: CapturedFrame) => {
    capturedFramesRef.current.push(frame);
    setFrameCount(capturedFramesRef.current.length);
  };

  const handleCameraError = (error: string) => {
    console.warn('Camera unavailable:', error);
    toast({
      title: 'Camera Unavailable',
      description: error,
    });
  };

  // Start audio recording via MediaRecorder.
  // Records a MIXED stream: user mic + agent voice from Anam.
  // Mic starts immediately; agent audio is attached as soon as the Anam video element exposes a captureStream.
  // Final blob is patched with fix-webm-duration so the player can seek.
  const startAudioRecording = async () => {
    try {
      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = micStream;

      const audioContext = new AudioContext();
      const destination = audioContext.createMediaStreamDestination();
      const micSource = audioContext.createMediaStreamSource(micStream);
      micSource.connect(destination);

      audioContextRef.current = audioContext;
      audioDestinationRef.current = destination;
      agentAudioAttachedRef.current = false;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const recorder = new MediaRecorder(destination.stream, { mimeType });

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      recorder.start(1000); // Capture every second
      mediaRecorderRef.current = recorder;
      audioRecordingStartRef.current = Date.now();

      // Non-blocking: try to attach agent audio as soon as Anam's <video> exposes a stream.
      attachAgentAudioToRecording();
    } catch (err) {
      console.warn('🎤 Mic permission denied or unavailable — session continues without audio recording:', err);
      // Don't break the session — audio is optional
    }
  };

  // Route the Anam agent's audio track into the recording mix as soon as it's available.
  // Two-pronged: (a) listen for the agent <video>'s `playing` event for an immediate trigger when Anam
  // actually has data, and (b) keep a 30s polling backstop for slow Anam connections.
  const attachAgentAudioToRecording = () => {
    const audioContext = audioContextRef.current;
    const destination = audioDestinationRef.current;
    if (!audioContext || !destination) return;

    const tryAttach = (): boolean => {
      if (agentAudioAttachedRef.current) return true;
      const agentVideo = document.getElementById('coaching-avatar-video') as HTMLVideoElement | null;
      if (!agentVideo || !agentVideo.videoWidth) return false;
      try {
        const agentMediaStream = (agentVideo as any).captureStream?.() || (agentVideo as any).mozCaptureStream?.();
        const audioTracks = agentMediaStream?.getAudioTracks?.() || [];
        if (audioTracks.length === 0) return false;
        const agentSource = audioContext.createMediaStreamSource(new MediaStream(audioTracks));
        agentSource.connect(destination);
        agentAudioAttachedRef.current = true;
        console.log('🎤 Agent audio attached to audio recording');
        return true;
      } catch (err) {
        console.warn('🎤 Failed to attach agent audio (will retry):', err);
        return false;
      }
    };

    // Event-driven: fire as soon as the agent video starts producing frames.
    const wireVideoListener = () => {
      const agentVideo = document.getElementById('coaching-avatar-video') as HTMLVideoElement | null;
      if (!agentVideo) return;
      const onPlaying = () => {
        if (tryAttach()) {
          agentVideo.removeEventListener('playing', onPlaying);
          agentVideo.removeEventListener('loadeddata', onPlaying);
        }
      };
      agentVideo.addEventListener('playing', onPlaying);
      agentVideo.addEventListener('loadeddata', onPlaying);
    };
    wireVideoListener();

    // Polling backstop: ~30s total, in case the video element doesn't exist yet at session start
    // or the events fire before we registered the listener.
    (async () => {
      for (let attempts = 0; attempts < 100; attempts++) {
        if (agentAudioAttachedRef.current) return;
        if (tryAttach()) return;
        // Re-wire listener if the video element appeared after our initial check.
        if (attempts === 5) wireVideoListener();
        await new Promise(r => setTimeout(r, 300));
      }
      console.warn('🎤 Could not attach agent audio after 30s — audio file will contain mic only');
    })();
  };

  // Stop audio recording and return the blob (or null).
  // Patches the webm with proper duration metadata so the player's scrubber can seek without playing through.
  const stopAudioRecording = (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      const startedAt = audioRecordingStartRef.current;

      const cleanup = () => {
        if (audioStreamRef.current) {
          audioStreamRef.current.getTracks().forEach(track => track.stop());
          audioStreamRef.current = null;
        }
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close().catch(() => {});
        }
        audioContextRef.current = null;
        audioDestinationRef.current = null;
        mediaRecorderRef.current = null;
        audioRecordingStartRef.current = null;
        agentAudioAttachedRef.current = false;
      };

      const buildAndPatch = async () => {
        if (audioChunksRef.current.length === 0) {
          cleanup();
          resolve(null);
          return;
        }
        const mimeType = audioChunksRef.current[0]?.type || 'audio/webm';
        const rawBlob = new Blob(audioChunksRef.current, { type: mimeType });
        const durationMs = startedAt ? Date.now() - startedAt : 0;

        if (mimeType.includes('webm') && durationMs > 0) {
          try {
            const fixed = await fixWebmDuration(rawBlob, durationMs);
            cleanup();
            resolve(fixed);
            return;
          } catch (err) {
            console.warn('Failed to patch webm duration — falling back to raw blob:', err);
          }
        }

        cleanup();
        resolve(rawBlob);
      };

      if (!recorder || recorder.state === 'inactive') {
        buildAndPatch();
        return;
      }

      recorder.onstop = () => {
        buildAndPatch();
      };

      recorder.stop();
    });
  };

  // Upload audio blob to Supabase Storage and return the public URL
  const uploadAudioBlob = async (blob: Blob, sessionId: string): Promise<string | null> => {
    try {
      const ext = blob.type.includes('webm') ? 'webm' : 'ogg';
      const filePath = `${sessionId}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('coaching-audio')
        .upload(filePath, blob, {
          contentType: blob.type,
          upsert: true,
        });

      if (uploadError) {
        console.error('Audio upload error:', uploadError);
        return null;
      }

      const { data: urlData } = supabase.storage
        .from('coaching-audio')
        .getPublicUrl(filePath);

      return urlData.publicUrl;
    } catch (err) {
      console.error('Audio upload failed:', err);
      return null;
    }
  };

  // Start split-screen video recording (user camera + avatar canvas)
  const startVideoRecording = async (): Promise<boolean> => {
    try {
      let agentVideo = document.getElementById('coaching-avatar-video') as HTMLVideoElement | null;
      for (let attempt = 0; attempt < 100; attempt++) {
        if (!sessionActiveRef.current) return false;
        agentVideo = document.getElementById('coaching-avatar-video') as HTMLVideoElement | null;
        if (agentVideo?.videoWidth && agentVideo.videoHeight) break;
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      if (!agentVideo?.videoWidth || !agentVideo.videoHeight || !sessionActiveRef.current) {
        console.warn('Video recording did not start because the coaching avatar stream was not ready');
        return false;
      }

      const testCanvas = document.createElement('canvas');
      if (typeof testCanvas.captureStream !== 'function') return false;

      // CameraCapture owns the learner preview. Use its explicit element rather
      // than a document-wide autoplay selector, which can resolve to the avatar
      // or another unrelated video on the page.
      const userVideo = userCameraVideoRef.current;

      const agentW = agentVideo.videoWidth;
      const agentH = agentVideo.videoHeight;
      const userW = userVideo?.videoWidth || 320;
      const userH = userVideo?.videoHeight || 240;
      const userScale = agentH / userH;
      const scaledUserW = Math.round(userW * userScale);

      const canvas = document.createElement('canvas');
      canvas.width = scaledUserW + agentW;
      canvas.height = agentH;
      canvasRef.current = canvas;
      const ctx = canvas.getContext('2d');
      if (!ctx) return false;

      const canvasStream = canvas.captureStream(30);

      // Combine audio: user mic + agent video audio
      const audioContext = new AudioContext();
      videoAudioContextRef.current = audioContext;
      const destination = audioContext.createMediaStreamDestination();

      if (audioStreamRef.current) {
        const micSource = audioContext.createMediaStreamSource(audioStreamRef.current);
        micSource.connect(destination);
      }

      try {
        const agentMediaStream = (agentVideo as any).captureStream?.() || (agentVideo as any).mozCaptureStream?.();
        if (agentMediaStream) {
          const audioTracks = agentMediaStream.getAudioTracks();
          if (audioTracks.length > 0) {
            const agentAudioSource = audioContext.createMediaStreamSource(new MediaStream(audioTracks));
            agentAudioSource.connect(destination);
          }
        }
      } catch {
        // Agent audio capture not available — continue without it
      }

      destination.stream.getAudioTracks().forEach(track => canvasStream.addTrack(track));

      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
        ? 'video/webm;codecs=vp9,opus'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
        ? 'video/webm;codecs=vp8,opus'
        : 'video/webm';

      const recorder = new MediaRecorder(canvasStream, {
        mimeType,
        videoBitsPerSecond: 1000000,
        audioBitsPerSecond: 64000,
      });

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) videoChunksRef.current.push(e.data);
      };

      const drawFrame = () => {
        if (ctx) {
          ctx.fillStyle = '#1a1a1a';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          // Camera permission may finish just after recording starts. Resolve
          // the current owned element per frame so the live preview replaces
          // the placeholder as soon as it becomes available.
          const currentUserVideo = userCameraVideoRef.current;
          if (cameraOnRef.current && currentUserVideo && currentUserVideo.videoWidth > 0 && !currentUserVideo.paused) {
            ctx.drawImage(currentUserVideo, 0, 0, scaledUserW, agentH);
          } else {
            ctx.fillStyle = '#333';
            ctx.fillRect(0, 0, scaledUserW, agentH);
            ctx.fillStyle = '#666';
            ctx.font = '24px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('Your Camera', scaledUserW / 2, agentH / 2);
          }
          if (agentVideo && !agentVideo.paused && !agentVideo.ended) {
            ctx.drawImage(agentVideo, scaledUserW, 0, agentW, agentH);
          }
        }
        if (videoRecorderRef.current?.state === 'recording') {
          requestAnimationFrame(drawFrame);
        }
      };

      recorder.start(1000);
      videoRecorderRef.current = recorder;
      videoRecordingStartRef.current = Date.now();
      drawFrame();

      // Progressive upload every 60 seconds
      uploadIntervalRef.current = setInterval(async () => {
        if (videoChunksRef.current.length > uploadedChunksRef.current) {
          const newChunks = videoChunksRef.current.slice(uploadedChunksRef.current);
          const chunkBlob = new Blob(newChunks, { type: 'video/webm' });
          const chunkIndex = uploadedChunksRef.current;
          uploadedChunksRef.current = videoChunksRef.current.length;
          try {
            await supabase.functions.invoke(
              `upload-recording?sessionId=${sessionId}&chunkIndex=${chunkIndex}`,
              { body: chunkBlob, headers: { 'Content-Type': 'video/webm' } }
            );
          } catch {
            // Non-blocking — chunk upload failure doesn't break session
          }
        }
      }, 60000);
      return true;
    } catch (err) {
      console.error('Failed to start video recording:', err);
      return false;
    }
  };

  // Stop video recording and upload final blob
  const stopVideoRecording = async (): Promise<string | null> => {
    if (uploadIntervalRef.current) {
      clearInterval(uploadIntervalRef.current);
      uploadIntervalRef.current = null;
    }
    const recorder = videoRecorderRef.current;
    await stopMediaRecorder(recorder);
    recorder?.stream.getTracks().forEach(track => track.stop());
    videoRecorderRef.current = null;

    if (videoAudioContextRef.current && videoAudioContextRef.current.state !== 'closed') {
      await videoAudioContextRef.current.close().catch(() => {});
    }
    videoAudioContextRef.current = null;

    if (videoChunksRef.current.length === 0) return null;

    try {
      const rawVideoBlob = new Blob(videoChunksRef.current, { type: 'video/webm' });
      const fileSizeMB = rawVideoBlob.size / 1024 / 1024;

      if (fileSizeMB > 200) {
        return null; // Too large to upload
      }

      // Patch EBML duration so the replay player can seek without playing through.
      const durationMs = videoRecordingStartRef.current ? Date.now() - videoRecordingStartRef.current : 0;
      videoRecordingStartRef.current = null;
      let videoBlob = rawVideoBlob;
      if (durationMs > 0) {
        try {
          videoBlob = await fixWebmDuration(rawVideoBlob, durationMs);
        } catch (err) {
          console.warn('Failed to patch video webm duration — uploading raw blob:', err);
        }
      }

      const { data: uploadResult, error: uploadError } = await supabase.functions.invoke(
        `upload-recording?sessionId=${sessionId}&final=true`,
        { body: videoBlob, headers: { 'Content-Type': 'video/webm' } }
      );

      if (!uploadError && uploadResult?.url) {
        return uploadResult.url;
      }
      return null;
    } catch {
      return null;
    }
  };

  // Fetch agent and template data
  useEffect(() => {
    async function fetchData() {
      if (!agentId || !user?.id) return;

      try {
        // Fetch agent
        const { data: agentData, error: agentError } = await supabase
          .from('simulation_agents')
          .select('*')
          .eq('id', agentId)
          .maybeSingle();

        if (agentError) throw agentError;
        if (!agentData) throw new Error('Agent not found');
        if (!agentData.anam_persona_id || agentData.type !== 'coach') {
          throw new Error('This agent is not configured for video coaching sessions');
        }

        // Fetch template
        let templateData: any = null;
        if (agentData.coaching_template_id) {
          const { data, error: templateError } = await supabase
            .from('coaching_templates')
            .select('*')
            .eq('id', agentData.coaching_template_id)
            .maybeSingle();

          if (templateError) throw templateError;
          templateData = data;
        }

        if (!templateData) {
          throw new Error('This coach does not have an output template configured');
        }

        const { data: authData } = await supabase.auth.getUser();
        const learnerName = getLearnerDisplayName(user, profile);
        const memory = await fetchCoachingMemoryProfile({
          authUserId: authData.user?.id,
          userId: user.id,
          learnerName,
          currentAgentId: agentData.id,
          currentTemplateId: templateData.id
        });

        setCoachingMemory(memory);
        setAgent(agentData);
        setTemplate(templateData);

      } catch (error: any) {
        console.error('Error fetching data:', error);
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive'
        });
        navigate('/simulations');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [agentId, user, profile, navigate, toast]);

  // Initialize Anam when agent and template are ready
  useEffect(() => {
    if (agent && template && !anam.isConnected) {
      anam.initialize();
      setCameraInitialized(true);
    }
  }, [agent, template, coachingMemory]);

  const handleStartSession = async () => {
    try {
      setIsRecording(true);
      setSessionStartTime(new Date());
      capturedFramesRef.current = [];
      injectedRuntimeKnowledgeRef.current.clear();
      setFrameCount(0);
      audioChunksRef.current = [];

      // Create coaching session record
      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) throw new Error('Not authenticated');

      const { error: sessionError } = await supabase
        .from('coaching_sessions')
        .insert({
          session_id: sessionId,
          tenant_id: agent?.tenant_id ?? tenant.tenant_id,
          agent_id: agentId!,
          template_id: template.id,
          user_id: user!.id,
          auth_user_id: authData.user.id,
          company_id: user?.company_id || null,
          status: 'in_progress',
          session_start: sessionStartTime?.toISOString() || new Date().toISOString(),
          platform: 'anam'
        });

      if (sessionError) throw sessionError;

      // Start Anam streaming
      await anam.startStreaming('coaching-avatar-video');
      sessionActiveRef.current = true;

      if (recordSession) {
        // Start audio recording (non-blocking — session continues even if mic is denied)
        await startAudioRecording();

        // Start shortly after streaming begins, then keep waiting for avatar
        // frames for up to 30 seconds on slower corporate connections.
        videoChunksRef.current = [];
        uploadedChunksRef.current = 0;
        videoRecordingStartTimerRef.current = setTimeout(() => {
          videoRecordingPromiseRef.current = startVideoRecording();
        }, 1000);
      }

      toast({
        title: 'Session Started',
        description: `Your coaching session with ${agent.name} has begun.`
      });

    } catch (error: any) {
      console.error('Error starting session:', error);
      reportUserFacingError(error, {
        userMessage: "We couldn't start your coaching session. This is usually a temporary connection issue with the coaching avatar.",
        area: 'coaching',
        action: 'start_session',
        metadata: { agent_id: agentId, agent_name: agent?.name },
      });
      setIsRecording(false);
      sessionActiveRef.current = false;
      if (videoRecordingStartTimerRef.current) {
        clearTimeout(videoRecordingStartTimerRef.current);
        videoRecordingStartTimerRef.current = null;
      }
      if (videoRecordingPromiseRef.current) {
        await videoRecordingPromiseRef.current.catch(() => false);
        videoRecordingPromiseRef.current = null;
      }
      await stopVideoRecording();
      await stopAudioRecording();
    }
  };

  // Sample frames evenly to prevent payload size issues
  const sampleFramesEvenly = (frames: CapturedFrame[], maxFrames: number = 30): CapturedFrame[] => {
    if (frames.length <= maxFrames) return frames;
    
    const step = frames.length / maxFrames;
    const sampled: CapturedFrame[] = [];
    
    for (let i = 0; i < maxFrames; i++) {
      const index = Math.floor(i * step);
      sampled.push(frames[index]);
    }
    
    return sampled;
  };

  const handleEndSession = async () => {
    // Guard against double-clicks
    if (isProcessingRef.current) {
      return;
    }
    isProcessingRef.current = true;
    setIsProcessing(true);
    // The learner expects the coach to become silent as soon as End Session is
    // pressed. Recorder finalisation can wait for a pending video start, so cut
    // off the active utterance synchronously before any await.
    anam.interruptPersona();
    if (autoEndTimerRef.current) {
      window.clearTimeout(autoEndTimerRef.current);
      autoEndTimerRef.current = null;
    }
    
    try {
      setIsRecording(false);
      sessionActiveRef.current = false;
      if (videoRecordingStartTimerRef.current) {
        clearTimeout(videoRecordingStartTimerRef.current);
        videoRecordingStartTimerRef.current = null;
      }
      if (videoRecordingPromiseRef.current) {
        await videoRecordingPromiseRef.current.catch(() => false);
        videoRecordingPromiseRef.current = null;
      }

      // Finalise recorders before stopping the underlying stream so the browser
      // can emit complete final chunks. Speech has already been interrupted.
      const videoUrl = recordSession ? await stopVideoRecording() : null;
      const audioBlob = recordSession ? await stopAudioRecording() : null;

      await anam.stopStreaming();

      // Upload audio blob
      let audioUrl: string | null = null;
      if (audioBlob && audioBlob.size > 1000) {
        audioUrl = await uploadAudioBlob(audioBlob, sessionId);
      }

      // Get transcript and consolidate word-by-word chunks into full conversation turns
      const rawTranscript = anam.getTranscript();
      const agentLabel = agent?.type
        ? `${agent.type.charAt(0).toUpperCase() + agent.type.slice(1)} ${agent.name}`
        : agent?.name || 'Coach';
      const consolidatedTranscript = consolidateTranscript(rawTranscript, agentLabel);

      // Build consolidated array format for DB storage (grouped by speaker, not word-by-word)
      const consolidatedArray: Array<{ role: string; content: string; timestamp: number }> = [];
      let currentRole = '';
      let currentContent = '';
      let currentTimestamp = 0;
      for (const msg of rawTranscript) {
        if (msg.role !== currentRole) {
          if (currentContent.trim()) {
            consolidatedArray.push({ role: currentRole, content: currentContent.trim(), timestamp: currentTimestamp });
          }
          currentRole = msg.role;
          currentContent = msg.content;
          currentTimestamp = msg.timestamp;
        } else {
          currentContent += (currentContent && !currentContent.endsWith(' ') ? ' ' : '') + msg.content;
        }
      }
      if (currentContent.trim()) {
        consolidatedArray.push({ role: currentRole, content: currentContent.trim(), timestamp: currentTimestamp });
      }

      // Get auth user for webhook
      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) throw new Error('Not authenticated');

      // Update session record with status 'generating'
      const updatePayload: Record<string, any> = {
        status: 'generating',
        session_end: new Date().toISOString(),
        conversation_transcript: consolidatedArray,
        platform: 'anam'
      };
      if (audioUrl) {
        updatePayload.audio_url = audioUrl;
      }
      if (videoUrl) {
        updatePayload.recording_url = videoUrl;
      }
      const { error: updateError } = await supabase
        .from('coaching_sessions')
        .update(updatePayload)
        .eq('session_id', sessionId);

      if (updateError) throw updateError;

      // Sample frames for vision analysis
      const sampledFrames = sampleFramesEvenly(capturedFramesRef.current, 30);
      let visionScores = null;
      let visionObservations = null;

      // Analyze vision frames if we have captured frames
      if (sampledFrames.length > 0 && agent?.enable_vision_evaluation) {
        toast({
          title: 'Analyzing Session',
          description: 'Processing visual feedback...'
        });

        try {
          const { data: visionData, error: visionError } = await supabase.functions.invoke(
            'analyze-vision-frames',
            {
              body: {
                session_id: sessionId,
                agent_id: agentId,
                agent_name: agent?.name,
                agent_type: 'coach',
                user_id: user?.id,
                auth_user_id: authData.user.id,
                user_name: (user as any)?.display_name || (user as any)?.username,
                user_email: user?.email,
                company_id: user?.company_id,
                frames: sampledFrames,
                vision_focus_areas: agent?.vision_focus_areas || ['eye_contact', 'posture', 'background', 'appearance'],
                vision_instructions: agent?.vision_instructions || ''
              }
            }
          );

          if (visionError) {
            console.error('Vision analysis error:', visionError);
          } else if (visionData) {
            visionScores = visionData.scores;
            visionObservations = visionData.observations;
          }
        } catch (visionErr) {
          console.error('Vision analysis failed:', visionErr);
        }
      }

      // Generate coaching document via edge function
      toast({
        title: 'Session Completed',
        description: 'Generating your coaching document...'
      });

      try {
        const { data: docResult, error: docError } = await supabase.functions.invoke(
          'generate-coaching-document',
          {
            body: { sessionId }
          }
        );

        if (docError) {
          console.error('Document generation error:', docError);
          toast({
            title: 'Warning',
            description: 'Document generation started - check results page for status.',
            variant: 'default'
          });
        } else {
        }
      } catch (docErr) {
        console.error('⚠️ Document generation error:', docErr);
        // Continue anyway - results page will show status
      }

      // Navigate to results with pathway context if present
      // pathwayId and moduleId are extracted from useSearchParams at component level
      const resultsUrl = pathwayId && moduleId
        ? `/coaching/results/${sessionId}?pathway=${pathwayId}&module=${moduleId}`
        : `/coaching/results/${sessionId}`;
      
      navigate(resultsUrl);

    } catch (error: any) {
      console.error('Error ending session:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      isProcessingRef.current = false;
      setIsProcessing(false);
      setSessionStartTime(null); // Reset timer state for next session
    }
  };

  autoEndSessionRef.current = () => {
    void handleEndSession();
  };

  useEffect(() => () => {
    if (autoEndTimerRef.current) {
      window.clearTimeout(autoEndTimerRef.current);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!agent || !template) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertDescription>
            Unable to load coaching session. Please try again.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <SessionPresentation>
    <div className="bg-background">
      <ResultGenerationDialog
        open={isProcessing}
        title="Generating your coaching results"
        status="Saving your session and preparing your coaching document."
      />

      <div className="session-page mx-auto w-full max-w-[1600px] space-y-3 px-3 py-3 md:px-5">
        <div className="session-heading flex items-center gap-3 rounded-xl border bg-card px-4 py-3">
          {agent.image_url && <img src={agent.image_url} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover" />}
          <div className="min-w-0 flex-1">
            <h1 className="text-base font-semibold">{agent.name}</h1>
            <p className="text-xs text-muted-foreground">{template.name}</p>
          </div>
          {isRecording && <span className="shrink-0 rounded-full bg-green-500/10 px-2 py-1 text-xs font-medium text-green-700">Live session</span>}
        </div>

        {anam.error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{anam.error}</AlertDescription>
          </Alert>
        )}

        <div className="session-camera-control flex items-center justify-between gap-3 rounded-xl border bg-card px-4 py-2">
          <div>
            <label htmlFor="coach-camera" className="font-medium">Your camera</label>
            <p className="text-xs text-muted-foreground">Microphone stays on. Turning camera off stops new video capture.</p>
            <details className="session-camera-details text-xs text-muted-foreground"><summary className="cursor-pointer">Camera and recording details</summary><p>Previously captured video and frames remain in this session.</p></details>
          </div>
          <Switch id="coach-camera" checked={cameraOn} onCheckedChange={setCameraOn} />
        </div>
        {/* Split Screen Video Container */}
        <div data-camera={cameraOn ? 'on' : 'off'} className={`session-stage grid grid-cols-1 ${cameraOn ? 'md:grid-cols-2' : ''} gap-3`}>
          {/* User Camera */}
          <Card className={cameraOn ? "session-video-tile min-w-0 overflow-hidden rounded-xl" : "hidden"}>
            <div className="relative h-full bg-gray-900">
              <CameraCapture
                isRecording={isRecording && cameraOn}
                fps={agent?.vision_analysis_fps || 0.5}
                onFrameCaptured={handleFrameCaptured}
                onError={handleCameraError}
                shouldInitialize={cameraInitialized && cameraOn}
                onVideoElementReady={handleCameraVideoElementReady}
              />
              {/* Label */}
              <div className="absolute bottom-4 left-4 bg-black/60 text-white px-3 py-1 rounded text-sm flex items-center gap-2">
                <Camera className="w-4 h-4" />
                You
              </div>
            </div>
          </Card>

          {/* Anam Avatar */}
          <Card className="session-video-tile min-w-0 overflow-hidden rounded-xl">
            <div className="relative h-full bg-gray-900">
              <video
                id="coaching-avatar-video"
                autoPlay
                playsInline
                className="session-avatar-video absolute inset-0 w-full h-full"
              />
              {!isRecording && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900/50">
                  <VideoOff className="w-20 h-20 text-gray-400" />
                </div>
              )}
              {/* Label */}
              <div className="absolute bottom-4 left-4 bg-black/60 text-white px-3 py-1 rounded text-sm flex items-center gap-2">
                <Video className="w-4 h-4" />
                {agent.type ? `${agent.type.charAt(0).toUpperCase() + agent.type.slice(1)} ` : ''}{agent.name}
              </div>
            </div>
          </Card>
        </div>

        {/* Session Timer — video sessions default 20min, audio default 60min */}
        {isRecording && (
          <div className="flex justify-center mb-4">
            <SessionTimer
              maxDurationSeconds={agent?.max_session_duration_seconds || (agent?.anam_persona_id ? 1200 : 3600)}
              startTime={sessionStartTime}
              isActive={isRecording}
              onTimeExpired={handleEndSession}
            />
          </div>
        )}

        {/* Frame count indicator during recording */}
        {isRecording && frameCount > 0 && (
          <div className="text-center mb-4 text-sm text-muted-foreground">
            Capturing visual feedback: {frameCount} frames
          </div>
        )}

        {/* Session controls stay together below the video. */}
        <div className="session-actions sticky bottom-0 z-10 flex flex-wrap items-center justify-center gap-2 rounded-xl border bg-background/95 px-3 py-2">
          {!isRecording ? (
            <Button
              size="lg"
              onClick={anam.error ? () => void anam.initialize() : handleStartSession}
              disabled={!anam.error && !anam.isConnected}
              className="min-w-64 px-8"
            >
              {!anam.error && !anam.isConnected ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Connecting to Coach…
                </>
              ) : anam.error ? (
                <>
                  <Video className="w-5 h-5 mr-2" />
                  Retry Coach Connection
                </>
              ) : (
                <>
                  <Video className="w-5 h-5 mr-2" />
                  Start Coaching Session
                </>
              )}
            </Button>
          ) : (
            <Button
              size="lg"
              variant="destructive"
              onClick={handleEndSession}
              disabled={isProcessing}
              className="min-w-64 px-8"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <VideoOff className="w-5 h-5 mr-2" />
                  End Session
                </>
              )}
            </Button>
          )}
          {!isRecording && !anam.error && !anam.isConnected && (
            <p className="text-sm text-muted-foreground">Preparing the secure coach connection…</p>
          )}
          {!isRecording && (
            <p className="text-xs text-muted-foreground">A missing camera does not prevent an audio-only coaching session.</p>
          )}
        </div>

        {/* Instructions */}
        {!isRecording && (
          <Card className="mb-6 p-6 bg-muted/50">
            <h3 className="font-semibold mb-3">Session Instructions:</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Allow microphone access. Camera access is optional</li>
              <li>• Speak clearly and naturally with your coach</li>
              <li>• The session will follow a structured intake process</li>
              <li>• A personalized {template.output_document} will be generated at the end</li>
              <li>• Session duration: approximately {template.estimated_duration || 30} minutes</li>
              {agent?.enable_vision_evaluation && (
                <li>• Your camera will provide visual feedback analysis</li>
              )}
            </ul>
            <div className="mt-5 rounded-lg border bg-background px-4 py-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-foreground">Save session recording</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Turn this off if you prefer not to store audio/video replay. Your coaching report will still be generated.
                  </p>
                </div>
                <Switch
                  checked={recordSession}
                  onCheckedChange={setRecordSession}
                  aria-label="Save coaching session recording"
                />
              </div>
            </div>
          </Card>
        )}

      </div>
    </div>
    </SessionPresentation>
  );
}
